import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../context/UserAuthContext';
import { FaUser, FaCheck, FaArrowRight, FaArrowLeft, FaStar, FaHeart, FaUsers } from 'react-icons/fa';
import './Profile.css';

const ProfileCompletionGuide = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { user, userProfile, updateUserProfile } = useUserAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Check if user needs profile completion guide
  const shouldShowGuide = () => {
    if (!user || !userProfile) return false;
    
    // Check session storage for completion status
    const sessionKey = `session_${user.uid}`;
    const hasActiveSession = sessionStorage.getItem(sessionKey);
    
    // If manually triggered (show prop is true), always show
    if (show) return true;
    
    // Don't show if already completed in this session
    if (hasActiveSession) return false;
    
    const userName = userProfile.displayName || user.displayName || '';
    
    // Check for generic usernames that indicate incomplete profile
    const isGeneric = /^(User|user)\d+$/.test(userName) || 
                     userName.trim() === '' || 
                     userName.includes('User') ||
                     userName.includes('user') ||
                     userName.length < 2;
    
    // Check if this is a phone number user with no display name
    const isPhoneUser = user.providerData?.[0]?.providerId === 'phone' && !userName.trim();
    
    // Check if this is a new Google user without proper display name
    const isNewGoogleUser = user.providerData?.[0]?.providerId === 'google.com' && 
                           (!userName.trim() || userName.includes('@'));
    
    return isGeneric || isPhoneUser || isNewGoogleUser;
  };

  useEffect(() => {
    if (show && userProfile) {
      setDisplayName(userProfile.displayName || user?.displayName || '');
      
      // Reset states when modal opens
      setCurrentStep(1);
      setError('');
      setSuccess('');
      setLoading(false);
      setIsCompleted(false);
    }
  }, [show, userProfile, user]);

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Mark as completed in session storage to prevent showing again
    if (user) {
      const sessionKey = `session_${user.uid}`;
      sessionStorage.setItem(sessionKey, 'active');
    }
    // For all visitors, mark the welcome guide as shown
    sessionStorage.setItem('welcomeGuideShown', 'true');
    onHide();
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    
    if (!displayName.trim()) {
      setError(t('profileCompletion.nameRequired'));
      return;
    }

    if (displayName.trim().length < 2) {
      setError(t('profileCompletion.nameMinLength'));
      return;
    }

    try {
      setLoading(true);
      
      await updateUserProfile({
        displayName: displayName.trim()
      });
      
      setSuccess(t('profileCompletion.updateSuccess'));
      setIsCompleted(true);
      
      // Move to success step
      setTimeout(() => {
        setCurrentStep(3);
      }, 1000);
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(t('profileCompletion.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    // Mark as completed in session storage using user-specific key
    if (user) {
      const sessionKey = `session_${user.uid}`;
      sessionStorage.setItem(sessionKey, 'active');
    }
    // For all visitors, mark the welcome guide as shown
    sessionStorage.setItem('welcomeGuideShown', 'true');
    onHide();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="profile-guide-step">
            <div className="profile-guide-icon">
              <FaUser size={48} />
            </div>
            <h3>{t('profileCompletion.title')}</h3>
            <p className="profile-guide-message">
              {user ? t('profileCompletion.introMessage') : t('profileCompletion.guestIntroMessage')}
            </p>
            
            <div className="profile-guide-benefits">
              <h5>{user ? t('profileCompletion.benefits') : t('profileCompletion.guestBenefits')}</h5>
              <div className="benefit-item">
                <FaHeart className="benefit-icon" />
                <span>{user ? t('profileCompletion.benefit1') : t('profileCompletion.guestBenefit1')}</span>
              </div>
              <div className="benefit-item">
                <FaUsers className="benefit-icon" />
                <span>{user ? t('profileCompletion.benefit2') : t('profileCompletion.guestBenefit2')}</span>
              </div>
              <div className="benefit-item">
                <FaStar className="benefit-icon" />
                <span>{user ? t('profileCompletion.benefit3') : t('profileCompletion.guestBenefit3')}</span>
              </div>
            </div>
          </div>
        );
      
      case 2:
        // Only show profile editing for logged-in users
        if (!user) {
          return (
            <div className="profile-guide-step">
              <div className="profile-guide-icon success">
                <FaCheck size={48} />
              </div>
              <h3>{t('profileCompletion.guestThankYouTitle')}</h3>
              <p className="profile-guide-message">
                {t('profileCompletion.guestThankYouMessage')}
              </p>
              
              <div className="profile-guide-next-steps">
                <p>{t('profileCompletion.guestNextSteps')}</p>
                <div className="guest-action-buttons">
                  <Button variant="outline-primary" onClick={() => window.location.href = '/login'}>
                    {t('profileCompletion.loginButton')}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => window.location.href = '/photos'}>
                    {t('profileCompletion.exploreButton')}
                  </Button>
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="profile-guide-step">
            <div className="profile-guide-icon">
              <FaUser size={48} />
            </div>
            <h3>{t('profileCompletion.editTitle')}</h3>
            <p className="profile-guide-message">
              {t('profileCompletion.editMessage')}
            </p>
            
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            
            <Form.Group className="profile-guide-form">
              <Form.Label>{t('profileCompletion.nameLabel')}</Form.Label>
              <Form.Control
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('profileCompletion.namePlaceholder')}
                className="profile-guide-input"
                autoFocus
                maxLength={50}
              />
              <Form.Text className="text-muted">
                {t('profileCompletion.nameHelp')}
              </Form.Text>
            </Form.Group>
            
            <div className="profile-guide-suggestions">
              <h6>{t('profileCompletion.suggestions')}</h6>
              <div className="suggestion-chips">
                {user?.providerData?.[0]?.providerId === 'google.com' && user.displayName && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setDisplayName(user.displayName)}
                    className="suggestion-chip"
                  >
                    {user.displayName}
                  </Button>
                )}
                {user?.providerData?.[0]?.providerId === 'phone' && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setDisplayName(t('profileCompletion.phoneUserSuggestion'))}
                    className="suggestion-chip"
                  >
                    {t('profileCompletion.phoneUserSuggestion')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="profile-guide-step">
            <div className="profile-guide-icon success">
              <FaCheck size={48} />
            </div>
            <h3>{t('profileCompletion.successTitle')}</h3>
            <p className="profile-guide-message">
              {t('profileCompletion.successMessage')}
            </p>
            
            <div className="profile-guide-preview">
              <div className="profile-preview-card">
                <div className="profile-preview-avatar">
                  {userProfile?.photoURL ? (
                    <img src={userProfile.photoURL} alt={displayName} />
                  ) : (
                    <FaUser size={24} />
                  )}
                </div>
                <div className="profile-preview-info">
                  <h6>{displayName}</h6>
                  <small className="text-muted">
                    {user?.providerData?.[0]?.providerId === 'google.com' 
                      ? t('profileCompletion.googleUser')
                      : t('profileCompletion.phoneUser')}
                  </small>
                </div>
              </div>
            </div>
            
            <p className="profile-guide-next-steps">
              {t('profileCompletion.nextSteps')}
            </p>
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <Button variant="outline-secondary" onClick={handleSkip}>
              {t('profileCompletion.skipButton')}
            </Button>
            <Button variant="primary" onClick={handleNextStep}>
              {user ? t('profileCompletion.getStarted') : t('profileCompletion.continueButton')}
              <FaArrowRight className="ms-2" />
            </Button>
          </>
        );
      
      case 2:
        // For guests, show different footer
        if (!user) {
          return (
            <Button variant="primary" onClick={handleComplete}>
              {t('profileCompletion.finishButton')}
            </Button>
          );
        }
        
        return (
          <>
            <Button variant="outline-secondary" onClick={handlePrevStep}>
              <FaArrowLeft className="me-2" />
              {t('profileCompletion.backButton')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveProfile}
              disabled={loading || !displayName.trim()}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t('profileCompletion.saving')}
                </>
              ) : (
                <>
                  {t('profileCompletion.saveButton')}
                  <FaArrowRight className="ms-2" />
                </>
              )}
            </Button>
          </>
        );
      
      case 3:
        return (
          <Button variant="primary" onClick={handleComplete}>
            {t('profileCompletion.finishButton')}
          </Button>
        );
      
      default:
        return null;
    }
  };

  if (!show) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="profile-completion-modal"
      size="lg"
    >
      <Modal.Header className="profile-guide-header">
        <div className="profile-guide-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            ></div>
          </div>
          <div className="progress-text">
            {t('profileCompletion.step', { current: currentStep, total: 3 })}
          </div>
        </div>
      </Modal.Header>
      
      <Modal.Body className="profile-guide-body">
        {renderStepContent()}
      </Modal.Body>
      
      <Modal.Footer className="profile-guide-footer">
        {renderFooter()}
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileCompletionGuide;